import Page from '@/components/Page';
import logger from '@/utils/logger.server';

// utilities
import { fetchPageBySlug, getRedirectPathFor, fetchAllPages, PageNotFoundError } from '@/utils/strapi';
import { detectLocale } from '@/utils/locale';
import { preparePath } from '@/utils/path';

export default function SlugPage(props) {
  return <Page {...props} />;
}

/**
 * getStaticPaths - generates all static paths at build time
 * Uses fallback: 'blocking' to generate pages on-demand if not pre-generated
 *
 * Note: This is called only during build time, not during revalidation
 */
export async function getStaticPaths() {
  // Check if we're in build mode (not revalidation)
  const isBuild = process.env.NODE_ENV === 'production' && !process.env.NEXT_RUNTIME;

  try {
    // Only fetch all pages during build, not during revalidation
    // During revalidation, return empty paths since we use fallback: 'blocking'
    if (!isBuild) {
      return {
        paths: [],
        fallback: 'blocking'
      };
    }

    logger.info('Generating static paths at build time...');

    // Fetch all pages from Strapi for Czech locale
    const csPages = await fetchAllPages('cs', false);
    // Fetch all pages from Strapi for English locale
    const enPages = await fetchAllPages('en', false);
    // Fetch all pages from Strapi for Polish locale
    const plPages = await fetchAllPages('pl', false);

    // Combine all pages
    const pages = [...csPages, ...enPages, ...plPages];

    // Generate paths for all pages including homepage
    const paths = pages.map((page) => {
      // Remove leading slash and split path into segments
      const pathSegments = page.path.replace(/^\//, '').split('/').filter(Boolean);

      return {
        params: {
          // Homepage: undefined slug, other pages: array of segments
          slug: pathSegments.length > 0 ? pathSegments : undefined
        }
      };
    });

    logger.info(`Generated ${paths.length} static paths at build time`);

    return {
      paths,
      fallback: 'blocking' // Generate pages on-demand for paths not in the list
    };
  } catch (error) {
    logger.error('Error generating static paths', {
      error: error.message,
      stack: error.stack,
      isBuild
    });
    // Return empty paths with fallback to allow on-demand generation
    return {
      paths: [],
      fallback: 'blocking'
    };
  }
}

export const getStaticProps = async ({ params }) => {
  const { slug } = await params;

  // slug je array pro catch-all routes, spojíme je do path
  // slug může být undefined (homepage) nebo array of segments
  const fullPath = !slug || slug.length === 0 ? '/' : `/${slug.join('/')}`;

  // Skip system paths that shouldn't be processed
  const skipPaths = ['/.well-known', '/favicon.ico', '/robots.txt', '/sitemap.xml', '/_next', '/api', '/admin'];

  const shouldSkip = skipPaths.some((skipPath) => fullPath.startsWith(skipPath));

  if (shouldSkip) {
    return { notFound: true };
  }

  // Detect locale and clean path
  const { locale, cleanPath } = detectLocale(fullPath);
  const { rootPath, restOfPath } = preparePath(cleanPath);

  logger.debug(`Request for path: ${cleanPath}`);

  try {
    const redirectPath = await getRedirectPathFor(cleanPath);
    if (redirectPath) {
      logger.info(`Redirect: ${cleanPath} -> ${redirectPath}`);
      return {
        redirect: {
          destination: redirectPath,
          permanent: true
        }
      };
    }
  } catch (error) {
    logger.error('Error checking for redirect', {
      path: cleanPath,
      error: error.message,
      stack: error.stack
    });
    // Continue to page fetch even if redirect check fails
  }

  try {
    const pageData = await fetchPageBySlug(rootPath, locale);

    if (!pageData.isContentReady) {
      logger.warn(`Page content not ready for path: ${rootPath}`);
      return { notFound: true };
    }

    return {
      props: {
        pageData,
        error: null,
        rootPath,
        restOfPath,
        locale,
        isPreview: false
      },
      // ISR: Revalidate every 1 hour (3600 seconds)
      revalidate: 3600
    };
  } catch (error) {
    // If page not found, return 404
    if (error instanceof PageNotFoundError) {
      logger.warn('Page not found', {
        path: rootPath,
        locale,
        fullPath
      });
      return { notFound: true };
    }

    logger.error('Error loading page', {
      path: rootPath,
      locale,
      fullPath,
      error: error.message,
      stack: error.stack,
      errorType: error.constructor.name
    });
    throw error;
  }
};
